<template>
  <v-container>
    <v-row>
      <v-col>
        <h1>Rechnungspläne</h1>
      </v-col>
      <v-col class="text-right">
        <v-btn
          color="primary"
          @click="showCreateDialog = true"
        >
          Neuer Rechnungsplan
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-data-table
          :headers="headers"
          :items="billingPlans"
          :loading="loading > 0"
          sort-by="name"
        >
          <template #item.price="{ item }">
            {{ formatPrice((item.billing_items && item.billing_items.length > 0) ? item.billing_items[0].price : 0) }}
          </template>
          <template #item.scope_type="{ item }">
            {{ getScopeLabels(item) }}
          </template>
          <template #item.per_device="{ item }">
            {{ getConditionPrice(item, 'per_device') }}
          </template>
          <template #item.per_tx="{ item }">
            {{ getConditionPrice(item, 'per_tx') }}
          </template>
          <template #item.interval="{ item }">
            {{ item.type === 'monthly' ? 'monatlich' : (item.type === 'yearly' ? 'jährlich' : item.type) }}
          </template>
          <template #item.description="{ item }">
            {{ (item.billing_items && item.billing_items.length > 0) ? item.billing_items[0].title : '-' }}
          </template>
          <template #item.actions="{ item }">
            <div class="text-no-wrap">
              <v-btn
                icon
                small
                @click="editPlan(item)"
              >
                <v-icon small>{{ mdiPencil }}</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                @click="confirmDelete(item)"
              >
                <v-icon small color="error">{{ mdiDelete }}</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog
      v-model="showCreateDialog"
      max-width="600px"
      @click:outside="resetForm"
    >
      <v-card>
        <v-card-title>
          {{ isEditing ? 'Rechnungsplan bearbeiten' : 'Neuer Rechnungsplan' }}
        </v-card-title>
        <v-card-text>
          <v-form
            ref="form"
            v-model="valid"
          >
            <v-text-field
              v-model="newPlan.name"
              label="Name"
              :rules="[v => !!v || 'Name ist erforderlich']"
              required
            />
            <v-select
              v-model="newPlan.type"
              :items="[
                { text: 'monatlich', value: 'monthly' },
                { text: 'jährlich', value: 'yearly' }
              ]"
              label="Typ"
            />
            <v-subheader class="px-0">
              Bedingungen
              <v-spacer />
              <v-menu offset-y>
                <template #activator="{ on, attrs }">
                  <v-btn
                    icon
                    v-bind="attrs"
                    v-on="on"
                    small
                  >
                    <v-icon>{{ mdiPlus }}</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    v-for="scope in availableScopes"
                    :key="scope.value"
                    @click="addScope(scope.value)"
                    :disabled="!!newPlan.conditions[scope.value]"
                  >
                    <v-list-item-title>{{ scope.text }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-subheader>

            <v-expansion-panels
              v-if="Object.keys(newPlan.conditions).length > 0"
              multiple
              class="mb-4"
            >
              <v-expansion-panel
                v-for="(categories, scopeKey) in newPlan.conditions"
                :key="scopeKey"
              >
                <v-expansion-panel-header>
                  {{ getScopeLabel(scopeKey) }}
                  <template #actions>
                    <v-btn
                      icon
                      small
                      @click.stop="removeScope(scopeKey)"
                    >
                      <v-icon color="error">{{ mdiDelete }}</v-icon>
                    </v-btn>
                    <v-icon>$expand</v-icon>
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div
                    v-for="(priceSteps, categoryKey) in categories"
                    :key="categoryKey"
                    class="mb-4 border-bottom"
                  >
                    <v-row no-gutters align="center">
                      <v-col class="font-weight-bold">
                        {{ getCategoryLabel(categoryKey) }}
                      </v-col>
                      <v-col class="text-right">
                        <v-btn
                          icon
                          small
                          @click="removeCategory(scopeKey, categoryKey)"
                        >
                          <v-icon color="error">{{ mdiDelete }}</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-row
                      v-for="(step, stepIndex) in priceSteps"
                      :key="stepIndex"
                      no-gutters
                      align="center"
                    >
                      <v-col cols="5">
                        <v-text-field
                          v-model.number="step[0]"
                          label="ab Menge"
                          type="number"
                          dense
                          class="mr-2"
                        />
                      </v-col>
                      <v-col cols="5">
                        <v-text-field
                          v-model.number="step[1]"
                          label="Preis (€)"
                          type="number"
                          step="0.01"
                          dense
                          class="mx-2"
                          :append-icon="mdiCurrencyEur"
                        />
                      </v-col>
                      <v-col cols="2" class="text-right">
                        <v-btn
                          icon
                          small
                          @click="removeStep(scopeKey, categoryKey, stepIndex)"
                          :disabled="priceSteps.length <= 1"
                        >
                          <v-icon small>{{ mdiMinus }}</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-btn
                      text
                      x-small
                      @click="addStep(scopeKey, categoryKey)"
                    >
                      <v-icon left x-small>{{ mdiPlus }}</v-icon>
                      Staffel hinzufügen
                    </v-btn>
                  </div>
                  <v-menu offset-y>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        text
                        small
                        v-bind="attrs"
                        v-on="on"
                        color="primary"
                      >
                        <v-icon left small>{{ mdiPlus }}</v-icon>
                        Preis-Kategorie hinzufügen
                      </v-btn>
                    </template>
                    <v-list max-height="300px" class="overflow-y-auto">
                      <v-list-item
                        v-for="cat in availableCategories"
                        :key="cat.value"
                        @click="addCategory(scopeKey, cat.value)"
                        :disabled="!!newPlan.conditions[scopeKey][cat.value]"
                      >
                        <v-list-item-title>{{ cat.text }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <div
              v-else
              class="text-caption text--secondary mb-4"
            >
              Keine Bedingungen definiert.
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="secondary"
            text
            @click="showCreateDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="primary"
            text
            :disabled="!valid"
            @click="isEditing ? savePlan() : createPlan()"
          >
            {{ isEditing ? 'Speichern' : 'Erstellen' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showDeleteDialog"
      max-width="400px"
    >
      <v-card>
        <v-card-title>Rechnungsplan löschen</v-card-title>
        <v-card-text>
          Sind Sie sicher, dass Sie den Rechnungsplan <strong>{{ planToDelete?.name }}</strong> löschen möchten?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showDeleteDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="error"
            text
            @click="deletePlan"
          >
            Löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { FETCH_BILLING_PLANS, CREATE_BILLING_PLAN, UPDATE_BILLING_PLAN, DELETE_BILLING_PLAN } from '@/store/action-types'
import { SET_SNACKBAR_MESSAGE } from '@/modules/common/store/mutation-types'
import { CONTRACT_PRODUCT_CLASSES } from "@/lib/device-db"
import { CONTRACT_SCOPE_ECOMMERCE, CONTRACT_SCOPE_KNB } from "@/store/contracts"
import { mdiPlus, mdiMinus, mdiDelete, mdiCurrencyEur, mdiPencil } from "@mdi/js"
import Vue from 'vue'

export default {
  name: 'AdminBilling',
  data () {
    return {
      showCreateDialog: false,
      showDeleteDialog: false,
      planToDelete: null,
      isEditing: false,
      editUrl: null,
      valid: false,
      newPlan: {
        name: '',
        type: 'monthly',
        conditions: {},
      },
      mdiPlus,
      mdiMinus,
      mdiDelete,
      mdiCurrencyEur,
      mdiPencil,
    }
  },
  computed: {
    ...mapState(['loading']),
    ...mapGetters('billing', ['billingPlans']),
    headers () {
      return [
        { text: 'Name', value: 'name' },
        { text: 'Typ', value: 'scope_type' },
        { text: 'Preis', value: 'price' },
        { text: 'Kosten pro Gerät', value: 'per_device' },
        { text: 'Kosten pro Transaktion', value: 'per_tx' },
        { text: 'Intervall', value: 'interval' },
        { text: 'Beschreibung', value: 'description' },
        { text: 'Aktionen', value: 'actions', sortable: false },
      ]
    },
    availableScopes () {
      return [
        { text: 'KNB', value: CONTRACT_SCOPE_KNB },
        { text: 'eCommerce', value: CONTRACT_SCOPE_ECOMMERCE },
      ]
    },
    availableCategories () {
      const categories = [
        { text: 'Kosten pro Transaktion', value: 'per_tx' },
        { text: 'Kosten pro Gerät', value: 'per_device' },
      ]
      Object.entries(CONTRACT_PRODUCT_CLASSES).forEach(([key, label]) => {
        categories.push({ text: label, value: key })
      })
      return categories
    },
  },
  async mounted () {
    await this.$store.dispatch('billing/' + FETCH_BILLING_PLANS)
  },
  methods: {
    getScopeLabel (scope) {
      return this.availableScopes.find(s => s.value === scope)?.text || scope
    },
    getScopeLabels (item) {
      if (!item.conditions) return '-'
      const scopes = Object.keys(item.conditions).map(this.getScopeLabel)
      return scopes.length > 0 ? scopes.join(', ') : '-'
    },
    getCategoryLabel (category) {
      return this.availableCategories.find(c => c.value === category)?.text || category
    },
    formatPrice (value) {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    getConditionPrice (item, category) {
      if (!item.conditions) return '-'

      let price = null
      // Check in all scopes (KNB, eCommerce)
      for (const scope in item.conditions) {
        if (item.conditions[scope][category]) {
          // Take the first step's price
          price = item.conditions[scope][category][0][1]
          break
        }
      }

      return price !== null ? this.formatPrice(price) : '-'
    },
    addScope (scope) {
      if (!this.newPlan.conditions[scope]) {
        Vue.set(this.newPlan.conditions, scope, {})
      }
    },
    removeScope (scope) {
      Vue.delete(this.newPlan.conditions, scope)
    },
    addCategory (scope, category) {
      if (!this.newPlan.conditions[scope][category]) {
        Vue.set(this.newPlan.conditions[scope], category, [[0, 0]])
      }
    },
    removeCategory (scope, category) {
      Vue.delete(this.newPlan.conditions[scope], category)
    },
    addStep (scope, category) {
      this.newPlan.conditions[scope][category].push([0, 0])
    },
    removeStep (scope, category, index) {
      this.newPlan.conditions[scope][category].splice(index, 1)
    },
    async createPlan () {
      if (this.$refs.form.validate()) {
        try {
          const payload = {
            name: this.newPlan.name,
            type: this.newPlan.type,
            conditions: this.newPlan.conditions,
          }
          await this.$store.dispatch('billing/' + CREATE_BILLING_PLAN, { data: payload })
          this.showCreateDialog = false
          this.resetForm()
          this.$store.commit(SET_SNACKBAR_MESSAGE, {
            message: 'Rechnungsplan erfolgreich erstellt.',
            color: 'success'
          })
        } catch (e) {
          this.$store.commit(SET_SNACKBAR_MESSAGE, {
            message: 'Fehler beim Erstellen des Rechnungsplans.',
            color: 'error'
          })
        }
      }
    },
    editPlan (item) {
      this.isEditing = true
      this.editUrl = item.url
      this.newPlan = JSON.parse(JSON.stringify({
        name: item.name,
        type: item.type,
        conditions: item.conditions || {}
      }))
      this.showCreateDialog = true
    },
    async savePlan () {
      if (this.$refs.form.validate()) {
        try {
          const payload = {
            name: this.newPlan.name,
            type: this.newPlan.type,
            conditions: this.newPlan.conditions,
          }
          await this.$store.dispatch('billing/' + UPDATE_BILLING_PLAN, {
            url: this.editUrl,
            data: payload
          })
          this.showCreateDialog = false
          this.resetForm()
          this.$store.commit(SET_SNACKBAR_MESSAGE, {
            message: 'Rechnungsplan erfolgreich aktualisiert.',
            color: 'success'
          })
        } catch (e) {
          this.$store.commit(SET_SNACKBAR_MESSAGE, {
            message: 'Fehler beim Aktualisieren des Rechnungsplans.',
            color: 'error'
          })
        }
      }
    },
    confirmDelete (item) {
      this.planToDelete = item
      this.showDeleteDialog = true
    },
    async deletePlan () {
      try {
        await this.$store.dispatch('billing/' + DELETE_BILLING_PLAN, {
          url: this.planToDelete.url
        })
        this.showDeleteDialog = false
        this.planToDelete = null
        this.$store.commit(SET_SNACKBAR_MESSAGE, {
          message: 'Rechnungsplan erfolgreich gelöscht.',
          color: 'success'
        })
      } catch (e) {
        this.$store.commit(SET_SNACKBAR_MESSAGE, {
          message: 'Fehler beim Löschen des Rechnungsplans.',
          color: 'error'
        })
      }
    },
    resetForm () {
      this.isEditing = false
      this.editUrl = null
      this.newPlan = {
        name: '',
        type: 'monthly',
        conditions: {},
      }
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
  },
}
</script>
